import type { ProjectManifest } from '@skyrover/shared/types';
import { PROJECT_FILE_EXTENSION, PROJECT_FORMAT_VERSION } from '@skyrover/shared/constants';

export interface SkyroverProjectFile {
  manifest: ProjectManifest;
  workspace: object;
}

export function createDefaultManifest(boardId: string, name = 'Untitled Project'): ProjectManifest {
  const now = new Date().toISOString();
  return {
    name,
    version: '1.0.0',
    formatVersion: PROJECT_FORMAT_VERSION,
    boardId,
    createdAt: now,
    modifiedAt: now,
  };
}

export function buildProjectFile(
  manifest: ProjectManifest,
  workspace: object,
  boardId: string,
  projectName: string,
): SkyroverProjectFile {
  const now = new Date().toISOString();
  return {
    manifest: {
      ...manifest,
      name: projectName,
      boardId,
      modifiedAt: now,
    },
    workspace,
  };
}

export function parseProjectFile(raw: unknown): SkyroverProjectFile {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid project file.');
  }

  const file = raw as Partial<SkyroverProjectFile>;
  const manifest = file.manifest;
  const workspace = file.workspace;

  if (!manifest || typeof manifest !== 'object') {
    throw new Error('Project file is missing a manifest.');
  }
  if (!manifest.name || typeof manifest.name !== 'string') {
    throw new Error('Project manifest is missing a name.');
  }
  if (manifest.formatVersion !== PROJECT_FORMAT_VERSION) {
    throw new Error(`Unsupported project format: ${String(manifest.formatVersion)}`);
  }
  if (!manifest.boardId || typeof manifest.boardId !== 'string') {
    throw new Error('Project manifest is missing a board ID.');
  }
  if (!workspace || typeof workspace !== 'object') {
    throw new Error('Project file is missing workspace data.');
  }

  return { manifest: manifest as ProjectManifest, workspace };
}

export function sanitizeProjectFilename(name: string): string {
  const trimmed = name.trim() || 'Untitled Project';
  const safe = trimmed.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 80);
  return `${safe || 'Untitled-Project'}${PROJECT_FILE_EXTENSION}`;
}

export function downloadProjectFile(project: SkyroverProjectFile, fileName?: string): void {
  const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName ?? sanitizeProjectFilename(project.manifest.name);
  link.click();
  URL.revokeObjectURL(url);
}

export async function readProjectFile(file: File): Promise<SkyroverProjectFile> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Project file is not valid JSON.');
  }
  return parseProjectFile(parsed);
}
